import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { useHasBody } from '../hooks/usePov';
import { useTopEdge } from './hudStack';

// WasdControls — an on-screen WASD keycap cluster for LARGER views (desktop /
// non-touch), where the mobile joystick isn't shown. Clicking/holding a key
// drives the SAME movement as the physical keys by dispatching real keydown/
// keyup events with the matching `code` (WalkControls / InteriorWalk read
// keys.current[e.code]) — so no movement logic is duplicated. Doubles as a
// visible affordance that the world is walkable.
const KEYS = { W: 'KeyW', A: 'KeyA', S: 'KeyS', D: 'KeyD', shift: 'ShiftLeft' };

function press(code, down) {
  window.dispatchEvent(new KeyboardEvent(down ? 'keydown' : 'keyup', { code, bubbles: true }));
}

// ── OPEN AT EVERY START, THEN IT STEPS ASIDE ─────────────────────────────────
// The cluster is the signal that the world is walkable, and for a mouse-only
// visitor it is the controls. Once someone has walked with the keyboard for a
// few seconds it fades out and folds into a small pill (the caps light up as
// the REAL keys are pressed, so they can see they work first).
//
// ⚠ PER VISIT, NOT FOREVER. The fold used to be remembered in localStorage, so
// after one session the keys never appeared again — Khari, 2026-10-23: "I did
// not see them when I started the app… They should start open, and move away
// gradually." Every load starts open; "hide" and the fold last for the visit;
// the pill brings them back.
const LEARN_MS = 3500;          // this much keyboard walking = they have it
const FADE_MS = 1400;           // the fade out before it folds

function Key({ label, wide = false, style, lit = false }) {
  const held = useRef(false);
  const code = KEYS[label];
  const down = (e) => {
    e.preventDefault();
    if (held.current) return;
    held.current = true;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    press(code, true);
  };
  const up = () => { if (!held.current) return; held.current = false; press(code, false); };
  return (
    <button
      onPointerDown={down} onPointerUp={up} onPointerCancel={up} onLostPointerCapture={up}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        gridArea: undefined, width: wide ? 108 : 48, height: 48, borderRadius: 10, cursor: 'pointer',
        font: '700 15px system-ui, sans-serif', color: '#ffe9c4', userSelect: 'none', touchAction: 'none',
        background: lit ? 'rgba(255,178,94,0.55)' : 'rgba(20,22,26,0.42)',
        border: `1px solid ${lit ? 'rgba(255,210,150,0.9)' : 'rgba(255,255,255,0.22)'}`,
        transform: lit ? 'translateY(1px)' : 'none', transition: 'background 0.08s, transform 0.08s',
        backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
        boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.3)',
        display: 'grid', placeItems: 'center', ...style,
      }}
    >
      {label}
    </button>
  );
}

export default function WasdControls() {
  const hasBody = useHasBody();   // V switches first / third person once you have a body
  const micVisible = useWorldStore((s) => s.micVisible);
  const [expanded, setExpanded] = useState(true);
  const [fading, setFading] = useState(false);
  const [lit, setLit] = useState({});
  const walked = useRef(0);
  const lastDown = useRef(0);
  const folded = useRef(false);   // folded once this visit: walking again does not re-fold it after a reopen
  const edge = useTopEdge('--hud-keys-top');   // captions stand on the keys (hudStack overKeys)
  const fold = () => {
    if (folded.current) return;
    folded.current = true;
    setFading(true);
    setTimeout(() => { setExpanded(false); setFading(false); }, FADE_MS);
  };

  const foldRef = useRef(fold);
  foldRef.current = fold;
  useEffect(() => {
    const CODES = new Set(Object.values(KEYS));
    // Only TRUSTED events count — the on-screen caps dispatch synthetic ones,
    // and clicking them must not be mistaken for knowing the keyboard.
    const down = (e) => {
      if (!CODES.has(e.code) || !e.isTrusted) return;
      setLit((l) => (l[e.code] ? l : { ...l, [e.code]: true }));
      if (!lastDown.current) lastDown.current = performance.now();
    };
    const up = (e) => {
      if (!CODES.has(e.code)) return;
      setLit((l) => (l[e.code] ? { ...l, [e.code]: false } : l));
      if (e.isTrusted && lastDown.current && e.code !== 'ShiftLeft') {
        walked.current += performance.now() - lastDown.current;
        lastDown.current = 0;
        if (walked.current > LEARN_MS) foldRef.current();
      }
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);

  // Stands on the player bar (--hud-player-h, components/PlayerBar), and above
  // the center quick-speak mic while it shows (AvatarChatDock: 54px tall at
  // max(20px, safe) over the bar). It kept a fixed +78px from before the bar,
  // when the mic moved up by the bar's height — and sat on the mic (2026-10-23).
  // And on the bottom-centre stack (BottomSlot: a session invite, Listen
  // together), which grows upward and doesn't know the keys are there.
  const over = micVisible
    ? 'calc(max(20px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px) + 66px)'
    : 'calc(max(16px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px))';
  const bottom = `max(${over}, calc(var(--hud-bottom-top, 0px) + 8px))`;
  if (!expanded) {
    return (
      <button
        ref={edge}
        onClick={() => { folded.current = true; setFading(false); setExpanded(true); }}
        title="Show the on-screen movement keys"
        style={{
          position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom, zIndex: 44,
          pointerEvents: 'auto', cursor: 'pointer', padding: '5px 12px', borderRadius: 999,
          font: '600 11px system-ui, sans-serif', color: 'rgba(255,233,196,0.75)', letterSpacing: 0.3,
          background: 'rgba(20,22,26,0.38)', border: '1px solid rgba(255,255,255,0.16)',
          backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
        }}
      >⌨ WASD · drag to look</button>
    );
  }
  return (
    <div ref={edge} style={{
      // Bottom-CENTER. Raised above the center quick-speak mic ONLY while it's
      // actually showing (AvatarChatDock) — drops back to the bottom when the mic
      // is gone (interiors, no target, guests).
      position: 'fixed', left: '50%', transform: 'translateX(-50%)',
      bottom,
      zIndex: 44, pointerEvents: fading ? 'none' : 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
      // Moves away gradually (Khari): it fades and settles down before folding.
      opacity: fading ? 0 : 1, transition: `opacity ${FADE_MS}ms ease, translate ${FADE_MS}ms ease`, translate: fading ? '0 14px' : '0 0',
    }}>
      <Key label="W" lit={lit.KeyW} />
      <div style={{ display: 'flex', gap: 6 }}>
        <Key label="A" lit={lit.KeyA} /><Key label="S" lit={lit.KeyS} /><Key label="D" lit={lit.KeyD} />
      </div>
      <Key label="shift" wide lit={lit.ShiftLeft} style={{ marginTop: 2, opacity: 0.9, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }} />
      <div style={{ fontSize: 10.5, opacity: 0.55, color: '#ffe9c4', letterSpacing: 0.3 }}>
        drag/arrow to look · shift to run{hasBody ? ' · V view' : ''}
        <span onClick={() => { folded.current = true; setExpanded(false); }} style={{ pointerEvents: 'auto', cursor: 'pointer', marginLeft: 8, textDecoration: 'underline' }}>hide</span>
      </div>
    </div>
  );
}
